import EditSharpIcon from "@mui/icons-material/EditSharp";
import { Box, Button, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Form, FormContext } from "components/shared/Form";
import { buildGenericModelForm } from "components/shared/GenericModelForm";
import { UpAndDownProvider } from "components/shared/UpAndDown";
import FormButtons, { FormButtonsBase } from "components/ui/atoms/FormButtons";
import CompanyField from "components/ui/atoms/formFields/CompanyField";
import EditorField from "components/ui/atoms/formFields/EditorField";
import PeopleField from "components/ui/atoms/formFields/PeopleField";
import RHFTextField from "components/ui/atoms/formFields/RHFTextField";
import UserField from "components/ui/atoms/formFields/UserField";
import CompanyDealsAndSourcings from "models/companies/CompanyDealsAndSourcings";
import { useLogHistory } from "models/history/apiHistory";
import NoteFormHeader from "models/notes/NoteFormHeader";
import NoteView from "models/notes/NoteView";
import {
  useCreateNote,
  useDeleteNote,
  useGetNote,
  useUpdateNote,
} from "models/notes/apiNote";
import { useContext, useEffect, useState } from "react";
import * as yup from "yup";

export const validationSchema = yup.object({
  owner: yup.string(),
  subject: yup.string().required(),
  content: yup.string().nullable(),
  companies_ids: yup.array().nullable(),
  companies_detail: yup.array().nullable(),
  people_ids: yup.array().nullable(),
  people: yup.array().nullable(),
});

export const getInitialValues = (object) => {
  return {
    id: object?.id || null,
    owner: object?.owner || null,
    owner_detail: object?.owner_detail || null,
    subject: object?.subject || "",
    content: object?.content || "",
    companies_ids: object?.companies_ids || [],
    companies_detail: object?.companies_detail || [],
    people_ids: object?.people_ids || [],
    people: object?.people || [],
    action_event: object?.action_event || null,
  };
};

export const NoteFormFields = () => {
  const { object } = useContext(FormContext);
  return (
    <>
      <Stack spacing={2} padding={2} pt="70px">
        <RHFTextField name="subject" label="Subject" fullWidth />
        <UserField name="owner" label="Owner" disabled />
        <CompanyField name="companies_ids" label="Companies" multiple creatable />
        <PeopleField name="people_ids" label="People" multiple creatable />
        <EditorField name="content" />
        <CompanyDealsAndSourcings
          companyIds={object.companies_ids}
          onlyActiveDealsAndSourcings
          widgetMode
        />
      </Stack>
      <FormButtons />
    </>
  );
};

const NoteForm = (props) => {
  return (
    <>
      {!props.hideHeader && (
        <NoteFormHeader
          formType={props.formType}
          onClose={props.onCancel}
          note={props.object}
        />
      )}
      <Form
        validationSchema={validationSchema}
        prepareInitialValues={getInitialValues}
        {...props}
      >
        <NoteFormFields />
      </Form>
    </>
  );
};

export const ModelNoteForm = buildGenericModelForm({
  form: NoteForm,
  useGet: useGetNote,
  useCreate: useCreateNote,
  useUpdate: useUpdateNote,
  useDelete: useDeleteNote,
});

export const NoteProvider = ({ id, onCancel, ...props }) => {
  const { data, isSuccess, isError } = useGetNote(id);

  if (isError) return <>Error while loading Deal with id='{id}'</>;
  if (!isSuccess) return <></>;

  return <NoteView note={data} onCancel={onCancel} {...props}></NoteView>;
};

export const ModelNoteFormWithView = ({
  formType,
  objectId,
  onCancel,
  onFinished,
  initialValues,
  values,
  mode,
  onModeChange,
  ...props
}) => {
  if (formType == "update" && !objectId)
    throw Error("Can't have formType = 'update' and not objectId");

  const theme = useTheme();
  const { mutateAsync } = useCreateNote();
  const [innerObjectId, setInnerObjectId] = useState(objectId);
  const [innerMode, setInnerMode] = useState();
  const [hadChanges, setHadChanges] = useState(false);
  const handleLog = useLogHistory("notes.note");
  useEffect(() => {
    innerObjectId && handleLog(innerObjectId);
  }, [innerObjectId]);

  useEffect(() => {
    setInnerMode(mode || (formType == "update" ? "view" : "create"));
  }, [mode]);

  useEffect(() => {
    onModeChange && onModeChange(innerMode);
  }, [innerMode]);

  const innerOnCancel = () => {
    if (hadChanges) return onFinished && onFinished();
    return onCancel && onCancel();
  };

  const createNote = () => {
    mutateAsync({ ...initialValues, subject: "[Title]" }).then((obj) => {
      setHadChanges(true);
      setInnerObjectId(obj.id);
      setInnerMode("update");
    });
  };

  return (
    <UpAndDownProvider
      values={values}
      onChange={(id) => {
        return setInnerObjectId(id);
      }}
    >
      {innerMode == "view" ? (
        <>
          <NoteProvider
            id={innerObjectId}
            onCancel={innerOnCancel}
            {...props}
          />
          <FormButtonsBase>
            <Button
              variant="contained"
              endIcon={<EditSharpIcon sx={{ scale: "0.8" }} />}
              onClick={() => setInnerMode("update")}
            >
              Edit
            </Button>
            <Button variant="outlined" onClick={innerOnCancel}>
              Cancel
            </Button>
          </FormButtonsBase>
        </>
      ) : innerMode == "update" ? (
        <>
          <ModelNoteForm
            {...props}
            key="update"
            formType="update"
            onCancel={() => {
              setInnerMode("view");
            }}
            onUpdated={() => setHadChanges(true)}
            onDeleted={onFinished}
            objectId={innerObjectId}
            autoSubmit
          />
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            height: 1,
          }}
        >
          <Button variant="contained" onClick={createNote}>
            Create
          </Button>
        </Box>
      )}
    </UpAndDownProvider>
  );
};
